/**
 * Team block — front-end behavior.
 */
import gsap from 'gsap';

/** Matches `breakpoint('small')` in _tokens.scss. */
const MOBILE_TEAM_MQ = '(max-width: 1000px)';

/** Bio clip transition in `front.scss` (grid-template-rows). */
const MOBILE_BIO_ANIM_MS = 500;

export class TeamFront {
	/**
	 * @param {HTMLElement} element Root `.team` section.
	 */
	constructor( element ) {
		this.element = element;
		this.mobileMq = window.matchMedia( MOBILE_TEAM_MQ );

		this.elements();
		this.sizing();
		this.bind();
	}

	elements() {
		this.sections = this.element.querySelectorAll( '.team-section' );
		this.sections.forEach( ( section ) => {
			section.wrapper = section.querySelector( '.team-cards' );
			section.cards = section.querySelectorAll( '[data-team-card]' );
			section.popout = section.querySelector( '.team-cards-popover' );
			section.isOpen = false;
			section.openCard = null;
			section.closeButton = section.querySelector( '.team-cards-popover-close' );

			section.cards.forEach( ( card ) => {
				card.picture = card.querySelector( '.team-card-picture-img' )?.src || '';
				card.name = card.querySelector( '.team-card-name' )?.textContent || '';
				card.role = card.querySelector( '.team-card-role' )?.innerText || '';
				card.bioEl = card.querySelector( '[data-team-card-bio]' );
				card.bio = card.bioEl?.textContent || '';
				card.bioClipEl = card.querySelector( '[data-team-card-bio-clip]' );
				card.pictureEl = card.querySelector( '.team-card-picture' );
				card.button = card.querySelector( '.team-card-bio-toggle' );
				card.closeButton = card.querySelector( '[data-team-bio-close]' );
				card.section = section;
			} );
		} );
	}

	isMobileTeamView() {
		return this.mobileMq.matches;
	}

	sizing() {
		if ( this.isMobileTeamView() ) {
			return;
		}

		this.sections.forEach( ( section ) => {
			if ( ! section.popout || ! section.cards.length ) {
				return;
			}

			section.popoutW = section.popout.offsetWidth;
			section.cardsW = section.cards[ 0 ].offsetWidth;
			section.wrapperW =
				section.cards[ section.cards.length - 1 ].offsetLeft +
				section.cards[ section.cards.length - 1 ].offsetWidth -
				section.cards[ 0 ].offsetLeft;
		} );
	}

	bind() {
		this.sections.forEach( ( section ) => {
			section.cards.forEach( ( card ) => {
				card.button?.addEventListener( 'click', () => {
					this.openCard( card );
				} );

				card.closeButton?.addEventListener( 'click', () => {
					this.closeCardMobile( card );
				} );
			} );
		} );

		this.sections.forEach( ( section ) => {
			section.closeButton?.addEventListener( 'click', () => {
				this.closeCard( section );
			} );
		} );

		this.mobileMq.addEventListener( 'change', () => {
			this.sections.forEach( ( section ) => {
				if ( section.openCard ) {
					this.resetMobileCard( section.openCard );
					section.openCard = null;
					section.isOpen = false;
				}
			} );
		} );
	}

	openCard( card ) {
		if ( this.isMobileTeamView() ) {
			this.openCardMobile( card );
			return;
		}

		this.openCardDesktop( card );
	}

	openCardDesktop( card ) {
		const tl = gsap.timeline();

		if ( card.section.isOpen ) {
			tl.to( card.section.popout, {
				autoAlpha: 0,
				duration: 0.3,
				ease: 'power2.inOut',
			} );
		}

		tl.set( card.section.popout, {
			clipPath: 'inset(0 50% 0 50%)',
			x: Math.min(
				Math.max(
					card.offsetLeft - ( card.section.popoutW / 2 ) + ( card.section.cardsW / 2 ),
					card.section.cards[ 0 ].offsetLeft
				),
				card.section.cards[ card.section.cards.length - 1 ].offsetLeft +
					card.section.cards[ card.section.cards.length - 1 ].offsetWidth -
					card.section.popoutW
			),
			autoAlpha: 0,
			scale: 1.1,
			onComplete: () => {
				card.section.popout.querySelector( '.team-cards-popover-picture' ).innerHTML =
					`<img src="${ card.picture }" alt="${ card.name }" />`;
				card.section.popout.querySelector( '.team-cards-popover-name' ).textContent = card.name;
				card.section.popout.querySelector( '.team-cards-popover-bio' ).textContent = card.bio;

				tl.to( card.section.popout, {
					autoAlpha: 1,
					scale: 1,
					duration: 1,
					ease: 'expo.out',
					clipPath: 'inset(0% 0% 0% 0%)',
				} );
			},
		} );

		card.section.isOpen = true;
	}

	openCardMobile( card ) {
		const section = card.section;

		if ( section.openCard === card ) {
			this.closeCardMobile( card );
			return;
		}

		if ( section.openCard ) {
			this.closeCardMobile( section.openCard, () => {
				this.openCardMobileNow( card );
			} );
			return;
		}

		this.openCardMobileNow( card );
	}

	openCardMobileNow( card ) {
		const section = card.section;

		if ( card.bioEl ) {
			card.bioEl.hidden = false;
		}

		if ( card.closeButton ) {
			card.closeButton.hidden = false;
		}

		requestAnimationFrame( () => {
			card.classList.add( 'is-open' );
		} );

		section.openCard = card;
		section.isOpen = true;
	}

	closeCard( section ) {
		if ( this.isMobileTeamView() ) {
			if ( section.openCard ) {
				this.closeCardMobile( section.openCard );
			}

			return;
		}

		const tl = gsap.timeline();

		tl.to( section.popout, {
			autoAlpha: 0,
			duration: 0.3,
			ease: 'power2.inOut',
		} );

		section.isOpen = false;
	}

	closeCardMobile( card, onComplete ) {
		const finish = () => {
			this.resetMobileCard( card );
			card.section.openCard = null;
			card.section.isOpen = false;
			onComplete?.();
		};

		if ( ! card.classList.contains( 'is-open' ) ) {
			finish();
			return;
		}

		card.classList.remove( 'is-open' );

		const clip = card.bioClipEl;
		if ( ! clip ) {
			window.setTimeout( finish, MOBILE_BIO_ANIM_MS );
			return;
		}

		let completed = false;
		const done = () => {
			if ( completed ) {
				return;
			}

			completed = true;
			clip.removeEventListener( 'transitionend', onTransitionEnd );
			finish();
		};

		const onTransitionEnd = ( event ) => {
			if ( event.target !== clip || event.propertyName !== 'grid-template-rows' ) {
				return;
			}

			done();
		};

		clip.addEventListener( 'transitionend', onTransitionEnd );
		window.setTimeout( done, MOBILE_BIO_ANIM_MS + 50 );
	}

	resetMobileCard( card ) {
		card.classList.remove( 'is-open' );

		if ( card.bioEl ) {
			card.bioEl.hidden = true;
		}

		if ( card.closeButton ) {
			card.closeButton.hidden = true;
		}
	}
}

